import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh, PointLight } from "three";

interface StarDeathProps {
  position: [number, number, number];
  hue?: number;
  /** Called once the death animation completes (~2s). */
  onComplete?: () => void;
}

/**
 * Animated star death: flickers and reddens, collapses to a bright point
 * with an expanding shockwave ring, then fades with drifting embers.
 * The whole sequence is ~2 seconds driven by useFrame.
 */
export function StarDeath({ position, hue = 210, onComplete }: StarDeathProps) {
  const meshRef = useRef<Mesh>(null);
  const lightRef = useRef<PointLight>(null);
  const startTime = useRef<number | null>(null);
  const completed = useRef(false);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = meshRef.current;
    const light = lightRef.current;
    if (!mesh || !light) return;

    if (t < 0.4) {
      // Phase 1: intensifying flicker, color shifts warmer
      const p = t / 0.4;
      const flicker = 1 + Math.sin(t * 60) * 0.15 * p;
      mesh.scale.setScalar(flicker * (1 + p * 0.3));
      light.intensity = 3 + p * 8;
      (mesh.material as any).emissiveIntensity = 2 + p * 6;
    } else if (t < 1.0) {
      // Phase 2: rapid collapse to bright point
      const p = (t - 0.4) / 0.6;
      const eased = p * p;
      mesh.scale.setScalar(Math.max(0.02, 1.3 * (1 - eased)));
      light.intensity = 11 - eased * 8;
      (mesh.material as any).emissiveIntensity = 8 - eased * 5;
    } else if (t < 2.0) {
      // Phase 3: point fades to nothing
      const p = (t - 1.0) / 1.0;
      const eased = p * p;
      mesh.scale.setScalar(Math.max(0, 0.02 * (1 - eased)));
      light.intensity = Math.max(0, 3 * (1 - eased));
      (mesh.material as any).emissiveIntensity = Math.max(0, 3 * (1 - eased));
    } else {
      mesh.scale.setScalar(0);
      light.intensity = 0;
      if (!completed.current) {
        completed.current = true;
        onComplete?.();
      }
    }
  });

  const color = `hsl(${hue}, 80%, 75%)`;
  const deathColor = `hsl(${Math.max(0, hue - 180)}, 90%, 70%)`;

  return (
    <group position={position}>
      <pointLight ref={lightRef} color={deathColor} intensity={0} distance={25} />
      <mesh ref={meshRef} scale={1}>
        <sphereGeometry args={[0.5, 24, 24]} />
        <meshStandardMaterial
          color={color}
          emissive={deathColor}
          emissiveIntensity={0}
          toneMapped={false}
        />
      </mesh>
      <ShockwaveRing hue={hue} />
      <Embers hue={hue} count={16} />
    </group>
  );
}

function ShockwaveRing({ hue }: { hue: number }) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const ring = ref.current;
    if (!ring) return;

    const delay = 0.4;
    if (t < delay) {
      (ring.material as any).opacity = 0;
      return;
    }

    const local = t - delay;
    if (local < 1.4) {
      const p = local / 1.4;
      ring.scale.setScalar(p * 10);
      (ring.material as any).opacity = Math.max(0, 0.7 - p * 0.7);
    } else {
      (ring.material as any).opacity = 0;
    }
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} scale={0}>
      <ringGeometry args={[0.8, 1.0, 32]} />
      <meshBasicMaterial
        color={`hsl(${Math.max(0, hue - 120)}, 95%, 75%)`}
        transparent
        opacity={0}
        toneMapped={false}
      />
    </mesh>
  );
}

function Embers({ hue, count }: { hue: number; count: number }) {
  const directions = useMemo(() => {
    const dirs: [number, number, number][] = [];
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + Math.random() * 0.4;
      const speed = 1.5 + Math.random() * 2.5;
      dirs.push([
        Math.cos(a) * speed,
        (Math.random() - 0.2) * speed * 0.5,
        Math.sin(a) * speed,
      ]);
    }
    return dirs;
  }, [count]);

  return (
    <>
      {directions.map((dir, i) => (
        <Ember key={i} direction={dir} hue={hue} />
      ))}
    </>
  );
}

function Ember({
  direction,
  hue,
}: {
  direction: [number, number, number];
  hue: number;
}) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = ref.current;
    if (!mesh) return;

    const delay = 0.35;
    if (t < delay) {
      mesh.scale.setScalar(0);
      return;
    }

    const local = t - delay;
    if (local < 1.5) {
      const p = local / 1.5;
      mesh.position.set(
        direction[0] * p,
        direction[1] * p - p * p * 0.8,
        direction[2] * p
      );
      mesh.scale.setScalar(Math.max(0, 0.06 * (1 - p * p)));
      (mesh.material as any).opacity = 1 - p;
    } else {
      mesh.scale.setScalar(0);
    }
  });

  const warmHue = Math.max(0, hue - 180 + Math.random() * 40 - 20);

  return (
    <mesh ref={ref} scale={0}>
      <sphereGeometry args={[1, 6, 6]} />
      <meshBasicMaterial
        color={`hsl(${warmHue}, 95%, 70%)`}
        transparent
        opacity={1}
        toneMapped={false}
      />
    </mesh>
  );
}
